import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import DashboardHeader from "../../components/dashboard-header";
import { useParams, useSearchParams } from "react-router";

type GuildGuidesPageProps = {
  access: "public" | "paid";
  inDashboard?: boolean;
};

const BLOG_BASE_URL = "https://blog.homedesignguild.com";
const BLOG_ORIGIN = new URL(BLOG_BASE_URL).origin;
const MIN_IFRAME_HEIGHT = 900;
const MAX_IFRAME_HEIGHT = 20000;
const HEIGHT_POLL_INTERVAL_MS = 900;
const HEIGHT_POLL_TIMEOUT_MS = 12000;
const FALLBACK_MANUAL_STEP = 1000;
const FALLBACK_MANUAL_MAX_HEIGHT = 16000;

const toSafeSlug = (rawSlug?: string) => {
  if (!rawSlug) return "";
  return String(rawSlug).replace(/[^a-zA-Z0-9-]/g, "");
};

const getGuildGuideUrl = (access: "public" | "paid", slug?: string) => {
  const safeSlug = toSafeSlug(slug);
  if (safeSlug) {
    return `${BLOG_BASE_URL}/post/${safeSlug}`;
  }
  return access === "paid"
    ? `${BLOG_BASE_URL}/guild-guides`
    : `${BLOG_BASE_URL}/homeowner-guild-guides`;
};

const clampHeight = (height: number) =>
  Math.max(MIN_IFRAME_HEIGHT, Math.min(MAX_IFRAME_HEIGHT, Math.round(height)));

const parseHeight = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 0 ? value : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const parsedNumber = Number(trimmed);
    if (Number.isFinite(parsedNumber) && parsedNumber > 0) {
      return parsedNumber;
    }

    try {
      const parsedJson = JSON.parse(trimmed);
      return parseHeight(parsedJson);
    } catch {
      const match = trimmed.match(/(?:height|iframeHeight|contentHeight)\s*[:=]\s*(\d+(?:\.\d+)?)/i);
      if (match?.[1]) {
        const extracted = Number(match[1]);
        return Number.isFinite(extracted) && extracted > 0 ? extracted : null;
      }
    }
  }

  if (value && typeof value === "object") {
    const candidate = value as Record<string, unknown>;
    return (
      parseHeight(candidate.height) ??
      parseHeight(candidate.iframeHeight) ??
      parseHeight(candidate.contentHeight) ??
      parseHeight(candidate.documentHeight) ??
      parseHeight(candidate.payload) ??
      parseHeight(candidate.data)
    );
  }

  return null;
};

export const GuildGuidesPage = ({ access, inDashboard = false }: GuildGuidesPageProps) => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const pollStopTimerRef = useRef<number | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [autoHeightSynced, setAutoHeightSynced] = useState(false);
  const [iframeHeight, setIframeHeight] = useState<number>(() =>
    clampHeight(typeof window !== "undefined" ? window.innerHeight * 1.15 : MIN_IFRAME_HEIGHT)
  );

  const resolvedAccess = useMemo<"public" | "paid">(() => {
    const content = (searchParams.get("content") || "").toLowerCase();
    if (content === "paid") return "paid";
    return access;
  }, [access, searchParams]);

  const iframeUrl = useMemo(
    () => getGuildGuideUrl(resolvedAccess, slug),
    [resolvedAccess, slug]
  );
  const title = resolvedAccess === "paid" ? "Guild Guides" : "Homeowner Guild Guides";

  const clearHeightPolling = useCallback(() => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (pollStopTimerRef.current) {
      window.clearTimeout(pollStopTimerRef.current);
      pollStopTimerRef.current = null;
    }
  }, []);

  const setHeightSafely = useCallback((nextHeight: number | null) => {
    if (!nextHeight) return;
    setAutoHeightSynced(true);
    setIframeHeight(clampHeight(nextHeight));
  }, []);

  const updateFromSameOriginDocument = useCallback(() => {
    let iframeDocument: Document | undefined;
    try {
      iframeDocument = iframeRef.current?.contentWindow?.document;
    } catch {
      return false;
    }
    if (!iframeDocument) return false;

    const documentElementHeight = iframeDocument.documentElement?.scrollHeight ?? 0;
    const bodyHeight = iframeDocument.body?.scrollHeight ?? 0;
    const measuredHeight = Math.max(documentElementHeight, bodyHeight);

    if (measuredHeight > 0) {
      setHeightSafely(measuredHeight);
      return true;
    }

    return false;
  }, [setHeightSafely]);

  const requestIframeHeight = useCallback(() => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;
    iframeWindow.postMessage(
      {
        type: "HDG_REQUEST_IFRAME_HEIGHT",
        source: "hdg-parent",
      },
      BLOG_ORIGIN
    );
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== BLOG_ORIGIN) return;
      setHeightSafely(parseHeight(event.data));
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [setHeightSafely]);

  useEffect(() => {
    setLoaded(false);
    setAutoHeightSynced(false);
    setIframeHeight(clampHeight(window.innerHeight * 1.15));
    clearHeightPolling();
  }, [iframeUrl, clearHeightPolling]);

  useEffect(() => () => clearHeightPolling(), [clearHeightPolling]);

  const handleIframeLoad = () => {
    setLoaded(true);
    clearHeightPolling();

    if (updateFromSameOriginDocument()) {
      return;
    }

    requestIframeHeight();

    pollTimerRef.current = window.setInterval(() => {
      if (!updateFromSameOriginDocument()) {
        requestIframeHeight();
      }
    }, HEIGHT_POLL_INTERVAL_MS);

    pollStopTimerRef.current = window.setTimeout(() => {
      clearHeightPolling();
    }, HEIGHT_POLL_TIMEOUT_MS);
  };

  return (
    <Box sx={{ backgroundColor: "#F5F5F5", minHeight: inDashboard ? "100vh" : "auto" }}>
      {inDashboard && <DashboardHeader title={title} showBackButton={false} />}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: iframeHeight,
          overflow: "hidden",
          border: "1px solid #E5E7EB",
          backgroundColor: "#fff",
          transition: loaded ? "height 160ms ease" : undefined,
        }}
      >
        {!loaded && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <CircularProgress size={28} sx={{ color: "#00A06D", mb: 1 }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading guide...
            </Typography>
          </Stack>
        )}
        <Box
          component="iframe"
          ref={iframeRef}
          src={iframeUrl}
          title={title}
          onLoad={handleIframeLoad}
          scrolling="no"
          sx={{
            width: "100%",
            height: "100%",
            border: 0,
            overflow: "hidden",
          }}
          allow="clipboard-read; clipboard-write"
        />
        {loaded && !autoHeightSynced && (
          <Box sx={{ position: "absolute", right: 12, bottom: 12, zIndex: 2 }}>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setIframeHeight((previousHeight) =>
                  clampHeight(Math.min(previousHeight + FALLBACK_MANUAL_STEP, FALLBACK_MANUAL_MAX_HEIGHT))
                );
              }}
              disabled={iframeHeight >= FALLBACK_MANUAL_MAX_HEIGHT}
              sx={{ backgroundColor: "#00A06D", "&:hover": { backgroundColor: "#00885D" } }}
            >
              Show More
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};
